<!--
  FilterOperatorsDropdown.vue

  Dropdown for selecting operators for filter conditions.
  Shows operators appropriate for the selected field type, organized by category,
  narrowed to the field's own operator allowlist when it has one.

  Works with useFilter composable - clicking an operator updates the condition.
-->
<template>
  <ToolbarDropdown
    :icon="getOperatorIcon(selected ?? '')"
    :label="selectedLabel"
    width="180px"
    align="left"
    size="small"
  >
    <div v-for="(group, idx) in operatorGroups" :key="idx" class="operators-section">
      <div class="section-label text-7">{{ group.label }}</div>
      <div
        v-for="op in group.operators"
        :key="op.key"
        class="dropdown-item operator-item text-7 pl-2 pr-3 py-1 g-1"
        :class="{ 'is-selected': op.key === selected }"
        @click="$emit('select', op.key)"
      >
        <font-awesome-icon :icon="getOperatorIcon(op.key)" class="operator-icon" />
        <span class="grow">{{ op.label }}</span>
        <font-awesome-icon v-if="op.key === selected" icon="check" class="check-icon" />
      </div>
    </div>
  </ToolbarDropdown>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { getOperatorGroups, OPERATORS } from "@auger/core/operators"
import ToolbarDropdown from "@auger/ui/components/ToolbarDropdown.vue"

const props = withDefaults(
  defineProps<{
    selected?: string | null
    fieldType?: string
    /** The field's operator allowlist; overrides the type's defaults. */
    allowed?: readonly string[] | null
  }>(),
  {
    selected: null,
    fieldType: "string",
    allowed: null,
  }
)

defineEmits<{
  select: [key: string]
}>()

const operatorGroups = computed(() => {
  const groups = getOperatorGroups(props.fieldType).map((group: any) => ({
    label: group.label,
    operators: group.operators.map((op: any) => {
      if (typeof op === "string") {
        return { key: op, label: OPERATORS[op]?.label || op }
      }
      return op
    }),
  }))
  if (!props.allowed) return groups

  // An allowlist keeps the type's groups (and their labels) for the operators
  // it names; the ones no group holds get a group of their own.
  const allowed = new Set(props.allowed)
  const kept = groups
    .map((group) => ({
      ...group,
      operators: group.operators.filter((op: any) => allowed.has(op.key)),
    }))
    .filter((group) => group.operators.length)
  const placed = new Set(kept.flatMap((group) => group.operators.map((op: any) => op.key)))
  const rest = props.allowed
    .filter((key) => !placed.has(key))
    .map((key) => ({ key, label: OPERATORS[key]?.label || key }))
  return rest.length ? [...kept, { label: "Relationship", operators: rest }] : kept
})

const selectedLabel = computed(() => {
  if (!props.selected) return "Select operator"

  for (const group of operatorGroups.value) {
    const op = group.operators.find((o: any) => o.key === props.selected)
    if (op) return op.label
  }

  return OPERATORS[props.selected]?.label || props.selected
})

function getOperatorIcon(operatorKey: string) {
  return OPERATORS[operatorKey]?.icon || "circle"
}
</script>

<style>
.operators-section {
  .section-label {
    font-weight: 600;
    text-transform: uppercase;
    color: var(--auger-text-weak);
    padding: 4px 12px;
    text-wrap: nowrap;
  }
}

.operator-item {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: var(--auger-bg-muted);
  }

  &.is-selected {
    background: var(--auger-primary-soft);
    font-weight: 500;
  }

  .operator-icon {
    width: 14px;
    color: var(--auger-text-weak);
  }

  .check-icon {
    width: 12px;
    color: var(--auger-primary);
  }
}
</style>
